import {DataService, FetchOptions, SortingRule} from 'src/services/types';
import {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {useLoader} from 'src/hooks/useLoader';
import {Duration, LoadState} from 'src/types/enums';
import {PageInfo} from 'src/views/college-portal/primary-market-states/types';
import {INITIAL_PAGE_INFO} from 'src/components/Table/utils';
import req from 'shared/req';
import {FormFieldError} from 'src/utils/errors/FormFieldError';
import {ApiErrorHandlerType} from 'src/utils/errors/ApiErrorHandler';

interface UseDataServiceProps<Type> {
  service: DataService<Type>;
  initialPageInfo?: PageInfo;
  searchColumns?: string[];
  generateFakeData?: () => Type;
}

export interface UseDataServiceState<Type> {
  blur: boolean;
  blurText?: string;
  data: Type;
  loadState: LoadState;
  pageInfo: PageInfo;
  setPage: (index: number) => void;
  setPageSize: (size: number) => void;
  setPageInfo: (pageInfo: PageInfo) => void;
  setSortRules: (sortRules: SortingRule<Type>[]) => void;
  query: string;
  setQuery: (event: ChangeEvent<HTMLInputElement>) => void;
}

const errorHandlers: ApiErrorHandlerType[] = [FormFieldError];

// eslint-disable-next-line -- TODO: fix PageType unused
export function useDataService<DataType extends object, PageType = DataType>(
  props: UseDataServiceProps<DataType>
): UseDataServiceState<DataType> {
  const defaultPageInfo = {...INITIAL_PAGE_INFO, ...(props.initialPageInfo || {})};
  const [pageIndex, setPage] = useState<number>(defaultPageInfo.pageIndex);
  const [pageSize, setPageSize] = useState<number>(defaultPageInfo.pageSize);
  const [sortBy, setSortRules] = useState<SortingRule<DataType>[]>([]);
  const [shouldBlur, setBlurState] = useState<boolean>(false);
  const [blurText, setBlurText] = useState<string>();
  const [query, setQuery] = useState<string>('');

  const loader = useCallback(() => {
    const fetchOptions: FetchOptions<DataType> = {
      sortBy,
      page: {pageIndex, pageSize},
      filter:
        query.length > 0 && Array.isArray(props.searchColumns)
          ? [
              {
                query,
                ids: props.searchColumns,
              },
            ]
          : [],
    };

    return props.service.fetch(req, fetchOptions);
  }, [pageIndex, pageSize, JSON.stringify(sortBy), query]);

  const [data, loadState, error] = useLoader<DataType>({
    errorHandlers,
    initialData: props.service.initialData as DataType,
    wait: Duration.HALF_SECOND,
    loadOptions: loader,
    getDataOnFail: props.generateFakeData,
  });

  useEffect(() => {
    if (error instanceof FormFieldError) {
      setBlurState(true);
      setBlurText(error.apiErrors[0].message);
    } else if (error) {
      setBlurState(true);
      setBlurText('There was a problem loading this data');
    } else if (shouldBlur) {
      setBlurState(false);
    }
  }, [error]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
  }, []);

  const handleSortChange = (rules: SortingRule<DataType>[]): void => {
    setPage(0);
    setSortRules(rules);
  };

  return {
    data,
    loadState,
    query,
    setQuery: handleQueryChange,
    blur: shouldBlur,
    blurText,
    setPage,
    setPageSize,
    setSortRules: handleSortChange,
    setPageInfo: (pageInfo: PageInfo): void => {
      setPageSize(pageInfo.pageSize);
      setPage(pageInfo.pageIndex);
    },
    pageInfo: {
      pageSize,
      pageIndex,
    },
  };
}
